import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, AlertCircle, ArrowRight } from 'lucide-react';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If already authenticated via AuthContext, redirect (no full reload)
  if (!authLoading && isAuthenticated) {
    return <Navigate to="/unified" replace />;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await authLogin(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/unified', { replace: true });
    } else {
      setError(result.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute top-20 -left-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16 text-white">
          <div className="flex items-center gap-4 mb-10">
            <img src="/logo.svg" alt="Imperion GRC" className="w-16 h-16 drop-shadow-lg" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                <span className="text-amber-400">Imperion</span> GRC
              </h1>
              <p className="text-white/60 text-sm">Governance, Risk & Compliance Platform</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold mb-4 leading-tight">
            Integrated Platform for<br />
            <span className="text-amber-300">Security & Compliance</span>
          </h2>

          <p className="text-lg text-white/70 mb-10">
            Multi-framework compliance management with AI-powered document analysis, 
            risk assessment, and real-time analytics.
          </p>

          <div className="space-y-3">
            {[
              'Multi-Framework GRC (ISO 27001, GDPR, NIS2, DORA...)',
              'AI-Powered Document Analysis & Control Extraction',
              'Risk Assessment & Treatment Plans',
              'Statement of Applicability (SoA)',
              'Audit Management & Evidence Collection',
              'Role-Based Access Control (RBAC)',
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                <span className="text-white/80 text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-xs text-white/40">
              Powered by Imperion &middot; Enterprise Security Solutions
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Imperion GRC" className="w-12 h-12" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  <span className="text-amber-500">Imperion</span> GRC
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">Governance, Risk & Compliance</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Sign in to access your compliance dashboard
            </p>
          </div>

          <div className="mt-8">
            <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700/50">
              <form className="space-y-6" onSubmit={handleLogin}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="input-professional pl-11"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      className="input-professional pl-11"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                      <span className="text-sm font-medium text-red-800 dark:text-red-300">{error}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Imperion GRC. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
